import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { type BlockerFunction } from 'react-router'
import { CHATBOT_NAME_LIMIT } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { BlockerStore } from 'shared/ui/Blocker'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IChatbotFullData } from 'entities/Chatbot/api/chatbotTypes'
import {
  ChatbotApi,
  ChatbotInstructionType,
  ChatbotStatus,
  type IChatbotPayload,
  UnifiedAgent,
} from 'entities/Chatbot'
import { sendEnhancePromptLogEvent } from 'entities/AIAgent/actions/enhancePrompt/sendEnhancePromptLogEvent'
import {
  unifiedAgentActivatedLogEvent,
  unifiedAgentChangesPublishedLogEvent,
  unifiedAgentCreateAbandonedLogEvent,
  unifiedAgentCreateOpenedLogEvent,
  unifiedAgentDisabledLogEvent,
  unifiedAgentEditActiveOpenedLogEvent,
  unifiedAgentSavedAsDraftLogEvent,
} from 'entities/AIAgent/actions/unifiedAgentEvents'
import { ChatbotPath, ChatbotRoutes } from 'entities/ExportRouters'
import { NameInputStore } from 'features/CreateOrEdit/NameInput'
import { showWebhookActionModal } from 'widgets/EnrollByWebhook/actions/webhookAction/webhookAction.tsx'
import { TriggerSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/TriggerSection/store/triggerSectionStore'
import { InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore'
import { BookingSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/store/bookingSectionStore'
import { hasGoogleCalendarErrors } from 'pages/chatbot/ui/UnifiedAgent/lib/googleCalendarErrors'
import { AutoFollowUpSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/AutoFollowUpSection/store/autoFollowUpSectionStore'
import { KnowledgeBaseSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/KnowledgeBaseSection/store/knowledgeBaseSectionStore'
import { chatbotOperation } from 'pages/chatbot/store/chatbotOperationStore'
import { replaceInlineActions } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import { ValidateSuggestionsModal } from 'pages/chatbot/ui/UnifiedAgent/ui/ValidateSuggestionsModal/ValidateSuggestionsModal'
import { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import { TestAgentStore } from 'pages/chatbot/ui/UnifiedAgent/ui/TestAgent/store/testAgentStore'
import { SectionModifiedTrackerStore } from 'pages/chatbot/ui/UnifiedAgent/store/sectionModifiedTrackerStore'

export type IAgentConfigureTab = typeof ChatbotRoutes.chat | typeof ChatbotRoutes.config

type EditableSection = 'knowledgeBase' | 'booking'

export class UnifiedAgentStore {
  id: number | null
  private readonly _isNewAgent: boolean
  readonly model: UnifiedAgent
  readonly nameInputStore: NameInputStore
  readonly triggerSectionStore: TriggerSectionStore
  readonly instructionsSectionStore: InstructionsSectionStore
  readonly bookingSectionStore: BookingSectionStore
  readonly autoFollowUpSectionStore: AutoFollowUpSectionStore
  readonly knowledgeBaseSectionStore: KnowledgeBaseSectionStore
  chatStore: ChatStore
  readonly testAgentStore: TestAgentStore
  readonly sectionModifiedTracker: SectionModifiedTrackerStore

  isSaving = false
  activeConfigureTab: IAgentConfigureTab = ChatbotRoutes.chat
  loadingSection: EditableSection | null = null
  validateTrigger = ''
  blockerStore: BlockerStore
  private _blockerConfig: {
    title: string
    primaryText: string
    primaryAction: (modalId: string) => Promise<unknown>
    additionalSecondaryActionText?: string
    shouldBlock?: BlockerFunction
    modalType: ModalTypeList
  } = {
    title: '',
    primaryText: '',
    primaryAction: async () => this.handleSaveAgent({ withError: true }),
    additionalSecondaryActionText: "Don't save",
    modalType: ModalTypeList.WARNING,
    shouldBlock: ({ nextLocation }) => {
      const { pathname } = nextLocation
      const isAgentTab =
        pathname.includes(`/${ChatbotRoutes.root}/`) &&
        (pathname.endsWith(`/${ChatbotRoutes.chat}`) ||
          pathname.endsWith(`/${ChatbotRoutes.config}`))
      return !isAgentTab
    },
  }
  constructor(
    agentId: number | null,
    readonly entities: IEntitiesStore,
    initialData?: IChatbotFullData
  ) {
    this.id = agentId
    this._isNewAgent = agentId === null
    this.model = new UnifiedAgent()
    this.nameInputStore = new NameInputStore({
      name: '',
      limit: CHATBOT_NAME_LIMIT,
      isResetErrorOnChange: true,
    })
    this.triggerSectionStore = new TriggerSectionStore(entities, this.model.trigger)
    this.bookingSectionStore = new BookingSectionStore(entities, this.model.booking)
    this.autoFollowUpSectionStore = new AutoFollowUpSectionStore(this.model.autoFollowUp)
    this.knowledgeBaseSectionStore = new KnowledgeBaseSectionStore(this.model.knowledgeBase)
    this.instructionsSectionStore = new InstructionsSectionStore(
      entities,
      agentId,
      this.model.instructions,
      undefined,
      this.requestUnifiedEnhance
    )
    this.chatStore = new ChatStore(this, entities)
    this.testAgentStore = new TestAgentStore(this)
    this.sectionModifiedTracker = new SectionModifiedTrackerStore(this)

    makeAutoObservable(this)
    if (initialData) {
      this.loadFromData(initialData)
    } else {
      unifiedAgentCreateOpenedLogEvent(entities)
      this.sectionModifiedTracker.start()
    }
    this._syncBlockerConfig()
    this.blockerStore = new BlockerStore(this, this._blockerConfig)
  }

  loadFromData = async (data: IChatbotFullData) => {
    this.model.syncOrigin(data)
    this._logOpenEvent(data)
    this.nameInputStore.setName(data.name)
    this.knowledgeBaseSectionStore.loadFromData(data)

    await Promise.all([
      this.triggerSectionStore.loadFromData(data),
      this.chatStore.loadFromData(data),
      this.bookingSectionStore.init(),
    ])

    runInAction(() => {
      this.instructionsSectionStore.setInstructions(this.model.instructions.instructions)

      this.blockerStore.updateCache()
      this.sectionModifiedTracker.start()
    })
  }

  get buildSessionId() {
    return this.chatStore.sessionId
  }

  get isConfigureTab() {
    return this.activeConfigureTab === ChatbotRoutes.config
  }

  get isChatLoading() {
    return this.chatStore.isLoading
  }

  get commonEnhancePayload() {
    return {
      chatbotId: this.id ?? 0,
      instruction_fields: this.instructionsSectionStore.existMergeFieldsPayload,
      booking_fields: this.bookingSectionStore.bookingFields,
      settings: {
        chatbot_type: ChatbotInstructionType.Unified,
        instructions: this.instructionsSectionStore.instructions,
        book_appointment_enabled: this.bookingSectionStore.bookingEnabled,
        book_appointment_type: this.bookingSectionStore.bookingType,
        enable_followup: this.autoFollowUpSectionStore.enabled,
        followup_assertiveness: this.autoFollowUpSectionStore.assertiveness,
        followup_window: this.autoFollowUpSectionStore.followUpWindow,
        use_knowledge_base: this.knowledgeBaseSectionStore.useKnowledgeBase,
        use_live_documents: this.knowledgeBaseSectionStore.useLiveDocuments,
      },
    }
  }

  get payload(): IChatbotPayload {
    return {
      ...this.model.toJSON(),
      enroll_by_webhook: this.triggerSectionStore.enrollByWebhookStore.isActive,
      name: this.nameInputStore.name,
      send_from: this.triggerSectionStore.sendFrom,
      filters: this.triggerSectionStore.paramsFilters,
      exclude_filters: this.triggerSectionStore.paramsExcludeFilters,
      knowledge_base_documents: this.knowledgeBaseSectionStore.documents,
      fields: this.instructionsSectionStore.existMergeFieldsPayload,
      creator_session_id: this.chatStore.sessionId ?? undefined,
      handoff_actions: [],
    }
  }

  get params() {
    const { creator_session_id: _, ...rest } = this.payload
    return {
      ...rest,
      runAutomatically: this.triggerSectionStore.runAutomatically,
      enroll_by_webhook: this.triggerSectionStore.enrollByWebhookStore.isActive,
    }
  }

  private _logOpenEvent = (data: IChatbotFullData) => {
    if (this.model.isActive) {
      unifiedAgentEditActiveOpenedLogEvent(this.entities, { agent_id: data.id })
    }
  }

  requestUnifiedEnhance = async (instruction: string, signal?: AbortSignal): Promise<string> => {
    sendEnhancePromptLogEvent(this.entities, {
      agentType: 'Unified Agent',
      agent_id: this.id ?? 0,
    })
    const { data } = await ChatbotApi.enhanceUnifiedPrompt(
      {
        ...this.commonEnhancePayload,
        settings: { ...this.commonEnhancePayload.settings, instructions: instruction },
        instructions: 'Enhance the prompt',
      },
      { signal }
    )
    return data.improvedPrompt
  }

  setActiveConfigureTab = (tab: IAgentConfigureTab) => {
    this.activeConfigureTab = tab
  }

  setIsSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  setValidateTrigger = () => {
    this.validateTrigger = nanoid()
  }

  titleValidate = () => {
    if (!this.nameInputStore.name) {
      this.nameInputStore.setError('Please add a name for your AI Agent.')
      return false
    }
    return true
  }

  configureTabRedirect = () => {
    if (this.activeConfigureTab === ChatbotRoutes.chat) {
      this.setActiveConfigureTab(ChatbotRoutes.config)

      const path = this.id ? `${ChatbotPath.editConfig(this.id)}` : `${ChatbotPath.newConfig}`
      uiStore.changeRoute({ path })
    }
  }

  saveValidate = () => {
    const inboxValid = !!this.triggerSectionStore.sendFrom
    if (!inboxValid) this.triggerSectionStore.setSendFromError('Select an inbox')
    const instructionsValid = !!this.instructionsSectionStore.instructions.trim()
    if (!instructionsValid)
      this.instructionsSectionStore.setError('Add instructions for your agent')

    return [
      this.titleValidate(),
      this.triggerSectionStore.validateEnrollment(),
      inboxValid,
      instructionsValid,
      this.bookingSectionStore.validateSection(),
    ].every(Boolean)
  }

  handleSaveAgent = async (props?: { withError?: boolean }) => {
    const withError = props?.withError
    this.setIsSaving(true)

    try {
      const id = await this.createAgent({ withError })
      if (id !== null) {
        this.sectionModifiedTracker.fireLogEvent()

        if (id && this.model.status === ChatbotStatus.Draft) {
          unifiedAgentSavedAsDraftLogEvent(this.entities, {
            build_session_id: this.buildSessionId,
            prompt_count: this.chatStore.promptCount,
            agent_id: id,
          })

          if (!withError) {
            const path = this.isConfigureTab ? ChatbotPath.editConfig(id) : ChatbotPath.editChat(id)
            uiStore.changeRoute({ path })
          }
        }
      }
    } finally {
      this.setIsSaving(false)
    }
  }

  private _syncBlockerConfig = () => {
    if (this.model.isActive) {
      this._blockerConfig.title = 'Publish agent before closing?'
      this._blockerConfig.primaryText = 'Publish changes'
      this._blockerConfig.primaryAction = async () => this.handlePublishAgent({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't publish"
    } else {
      this._blockerConfig.title = 'Save agent before closing?'
      this._blockerConfig.primaryText =
        this.model.status === null ? 'Save as draft' : 'Save changes'
      this._blockerConfig.primaryAction = async () => this.handleSaveAgent({ withError: true })
      this._blockerConfig.additionalSecondaryActionText = "Don't save"
    }
  }

  createAgent = async (props?: { withError?: boolean }) => {
    const withError = props?.withError

    if (!this.titleValidate()) {
      if (withError) throw new Error('Validation failed')
      return null
    }

    try {
      const isUpdate = !!this.id
      const { data } = this.id
        ? await chatbotOperation.update(this.id, this.payload)
        : await chatbotOperation.create(this.payload)

      toastStore.add({ title: 'AI Agent saved', type: 'info' })

      return runInAction(() => {
        this.id = data.id
        this.model.syncOrigin(data)
        this.blockerStore.updateCache()
        this._syncBlockerConfig()
        return isUpdate ? undefined : data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error)

      logger.error(error)
      toastStore.add({
        title: 'Something went wrong',
        desc: 'Please try again',
        type: 'error',
      })

      this.configureTabRedirect()

      if (withError) throw error
      return null
    }
  }

  private _syncErrors = (error: AxiosError) => {
    const nameError = error.errors.getError('name')
    const sendFromError =
      error.errors.getError('send_from') || error.errors.getError('send_from.options')
    const instructionsError = error.errors.getError('settings.instructions.value')
    const bookingLinkError = error.errors.getError('settings.book_appointment_link.value')
    const hasGoogleCalendarError = hasGoogleCalendarErrors(error.errors)
    const knowledgeBaseError =
      error.errors.getError('knowledge_base_documents') ||
      error.errors.getError('settings.use_knowledge_base')

    if (nameError) this.nameInputStore.setError(nameError)
    if (sendFromError) this.triggerSectionStore.setSendFromError(sendFromError)
    if (instructionsError) this.instructionsSectionStore.setError(instructionsError)
    if (bookingLinkError) this.bookingSectionStore.handleBookingLinkError(bookingLinkError)
    if (hasGoogleCalendarError) this.bookingSectionStore.handleGoogleCalendarErrors(error.errors)
    if (knowledgeBaseError) this.knowledgeBaseSectionStore.setDocumentsError(knowledgeBaseError)

    this.setValidateTrigger()
  }

  createAndPublishAgent = async (props?: { withError?: boolean }) => {
    const withError = props?.withError

    if (!this.saveValidate()) {
      this.setValidateTrigger()
      this.configureTabRedirect()
      if (withError) throw new Error('Validation failed')
      return null
    }
    if (this.model.knowledgeBase.useKnowledgeBase) {
      const validateAgentKnowledgeBaseStore =
        this.knowledgeBaseSectionStore.agentKnowledgeBaseStore.checkValidate()
      if (!validateAgentKnowledgeBaseStore) {
        this.setValidateTrigger()
        if (withError) throw new Error('Validation failed')
        return null
      }
    }

    const hasSuggestions = await this._validateBeforePublish()
    if (hasSuggestions) {
      if (withError) throw new Error('Suggestions pending')
      return null
    }
    const wasActive = this.model.isActive
    const wasDraft = this.model.status === ChatbotStatus.Draft

    try {
      const { data } = this.id
        ? await chatbotOperation.update(this.id, { ...this.payload, is_active: true })
        : await chatbotOperation.create({ ...this.payload, is_active: true })

      runInAction(() => {
        this.id = data.id
        this.model.syncOrigin(data)
        this._syncBlockerConfig()
        this.blockerStore.updateCache()
      })

      if (data.is_active) {
        await chatbotOperation.enroll(
          data.id,
          data.name,
          data.filters,
          undefined,
          data.exclude_filters
        )

        if (data.enroll_by_webhook && data.webhook_url) {
          showWebhookActionModal({
            webhookUrl: data.webhook_url,
            sourceName: data.name,
            source: 'AI Agent',
          })
        }
      }

      if (wasActive) {
        unifiedAgentChangesPublishedLogEvent(this.entities, { agent_id: data.id })
      } else {
        unifiedAgentActivatedLogEvent(this.entities, {
          agent_id: data.id,
          activated_from_draft: wasDraft,
          prompt_count: this.chatStore.promptCount,
        })
      }

      this.sectionModifiedTracker.fireLogEvent()

      return data.id
    } catch (error) {
      if (error instanceof AxiosError) this._syncErrors(error)

      logger.error(error)
      toastStore.add({
        title: 'Failed to activate',
        desc: 'Complete all required fields',
        type: 'error',
      })

      this.configureTabRedirect()

      if (withError) throw error
      return null
    }
  }

  handleDisableAgent = async () => {
    if (this.id === null) return

    try {
      const data = await chatbotOperation.disable(this.id)
      if (!data) return

      runInAction(() => {
        this.model.syncOrigin(data.data)
        this._syncBlockerConfig()
        this.blockerStore.updateCache()
      })

      unifiedAgentDisabledLogEvent(this.entities, {
        agent_id: this.id,
      })
    } catch (error) {
      logger.error(error)
    }
  }

  handlePublishAgent = async (props?: { withError?: boolean }) => {
    const withError = props?.withError
    this.setIsSaving(true)

    try {
      const id = await this.createAndPublishAgent({ withError })
      if (id && !withError)
        uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}` })
    } finally {
      this.setIsSaving(false)
    }
  }

  private _validateBeforePublish = async (): Promise<boolean> => {
    const modalId = 'validateUnifiedAgentModal'
    try {
      const { data } = await ChatbotApi.validateUnifiedAgent(this.commonEnhancePayload)
      if (!data.suggestions.length) return false

      const mergeFields = this.entities.mergeFieldStore.allFields
      const usersMap = this.entities.usersStore.usersMap
      const suggestions = data.suggestions.map(({ text }) =>
        replaceInlineActions(text, mergeFields, usersMap)
      )

      return new Promise<boolean>((resolve) => {
        const handleEdit = () => {
          modalStore.closeModal(modalId)
          resolve(true)
          this.configureTabRedirect()
        }
        const handleSkip = () => {
          modalStore.closeModal(modalId)
          resolve(false)
        }

        modalStore.addModal({
          id: modalId,
          width: 480,
          showHeader: false,
          showCloseButton: false,
          showCloseIcon: false,
          hiddenContent: true,
          paddingContent: '0',
          disabledOnAllClose: true,
          disabledOverlayClose: true,
          disabledOnEscClose: true,
          ModalContent: ValidateSuggestionsModal,
          ModalContentProps: { handleEdit, suggestions, handleSkip },
        })
      })
    } catch (error) {
      logger.error(error)
      return false
    }
  }

  updateInstructions = async (section: EditableSection) => {
    let instructions = ''
    let sectionSync = null

    if (section === 'knowledgeBase') {
      if (!this.knowledgeBaseSectionStore.validateSection()) return
      instructions = this.knowledgeBaseSectionStore.sectionInstruction
      sectionSync = this.knowledgeBaseSectionStore.init
    } else if (section === 'booking') {
      if (!this.bookingSectionStore.validateSection()) return
      instructions = this.bookingSectionStore.sectionInstruction
      sectionSync = this.bookingSectionStore.init
    }

    if (!sectionSync) return

    this.loadingSection = section
    try {
      const { data } = await ChatbotApi.enhanceUnifiedPrompt({
        ...this.commonEnhancePayload,
        settings: {
          ...this.commonEnhancePayload.settings,
          ...this.bookingSectionStore.enhancePayload(section === 'booking'),
          ...this.knowledgeBaseSectionStore.enhancePayload(section === 'knowledgeBase'),
        },
        instructions,
      })
      runInAction(() => {
        this.instructionsSectionStore.setInstructions(data.improvedPrompt)
        sectionSync()
        this.sectionModifiedTracker.markSectionModified(section)
      })
    } catch (error) {
      toastStore.add({
        title: 'Something went wrong',
        desc: 'Please try again',
        type: 'error',
      })
      logger.error(error)
    } finally {
      runInAction(() => {
        this.loadingSection = null
      })
    }
  }

  resetEditSection = (section: string) => {
    switch (section) {
      case 'knowledgeBase':
        this.knowledgeBaseSectionStore.resetSection()
        break
      case 'booking':
        this.bookingSectionStore.resetSection()
        break
      default:
        break
    }
  }

  saveForTest = async (): Promise<number | null> => {
    const isNew = this.id === null
    const isActive = this.model.isActive
    const result = await (isActive ? this.createAndPublishAgent() : this.createAgent())
    if (result === null) return null
    if (!isActive) this.sectionModifiedTracker.fireLogEvent()

    if (isNew && result) {
      if (!this.model.isActive) {
        unifiedAgentSavedAsDraftLogEvent(this.entities, {
          build_session_id: this.buildSessionId,
          prompt_count: this.chatStore.promptCount,
          agent_id: result,
        })
      }

      uiStore.changeRoute({
        path: ChatbotPath.editChat(result),
        options: { state: { autoStartTest: true } },
      })
      return null
    }
    return this.id
  }

  dispose = () => {
    if (this._isNewAgent && this.id === null) {
      unifiedAgentCreateAbandonedLogEvent(this.entities, {
        last_tab: this.activeConfigureTab,
        prompt_count: this.chatStore.promptCount,
        build_session_id: this.buildSessionId,
      })
      this.sectionModifiedTracker.fireLogEvent()
    }

    this.sectionModifiedTracker.dispose()
    this.bookingSectionStore.dispose()
    this.instructionsSectionStore.dispose()
    this.triggerSectionStore.dispose()
  }
}
